/**
 * @fileoverview Conflict-check party schedule (MOR-882, MOR-992)
 *
 * Purpose: render the party groups a conflict-check brief defers rather than
 * lists inline — creditors, and any other role too long for the brief. The
 * same groups go out as a CSV attached to the vote email; this is the on-screen
 * mirror, so a voter who reads the brief in the browser sees the parties
 * without hunting for the attachment.
 *
 * Secured creditors carry their PPSR registrations (MOR-992): each group
 * header tallies PMSI against non-PMSI registrations — the distinction that
 * actually matters for conflict, a PMSI being far less of an issue than a
 * general security interest — and each party renders as a card with its
 * registrations badged and detailed.
 *
 * Used by:
 *  - src/routes/conflict-check.$token.tsx (public vote page)
 *  - src/routes/_app/_auth/dashboard/-ui.job-conflict-check-panel.tsx
 *  - src/components/pre-appointment/pipeline-job-panel.tsx (in-app card)
 */

import { cn } from "@/utils/misc";
import { ConflictPartyGroup } from "./conflict-party-group";
import type { ConflictPartyListView } from "./conflict-party-schedule.helpers";

export type { ConflictPartyListView } from "./conflict-party-schedule.helpers";

interface ConflictPartyScheduleProps {
  partyLists: ConflictPartyListView[] | undefined;
  /** Note that the same lists are attached to the vote email. */
  showAttachmentNote?: boolean;
  className?: string;
}

/**
 * @component
 * @example
 * <ConflictPartySchedule partyLists={check.partyLists} showAttachmentNote />
 */
export function ConflictPartySchedule({
  partyLists,
  showAttachmentNote = false,
  className,
}: ConflictPartyScheduleProps) {
  if (!partyLists || partyLists.length === 0) return null;

  return (
    <section className={cn("grid gap-1.5", className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="text-xs font-medium text-muted-foreground">
          Party schedule
        </p>
        {showAttachmentNote && (
          <span className="text-[11px] text-muted-foreground/80">
            Also attached to the vote email as a spreadsheet
          </span>
        )}
      </div>
      {partyLists.map((list) => (
        <ConflictPartyGroup key={list.label} list={list} />
      ))}
    </section>
  );
}
